/**
 * Private component used only by EntityInput, for rendering suggestions in the dropdown
 */
import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {FONT_PROXIMA_11} from 'styles/FontConstants';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {NEAR_BLACK} from 'ui-library/Colors';
import {isCustomParty} from 'ui-library/entity-input/EntityInputUtil';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {typeToName} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

import {CustomParty, EntitySuggestion} from './EntityInputConstants';

// We need to prevent default on mouseDown, otherwise focus is lost (causing the list to
// disappear via onBlur --> toggleFocus) and this view is destroyed.
function handleMouseDown(e: ElementMouseEvent): void {
  e.preventDefault();
}

export function getPartyDisplayName(suggestion: EntitySuggestion): string {
  if (suggestion == null) {
    return '';
  }
  return suggestion.name || suggestion.text || suggestion.handle || 'Loading...';
}

function EntitySuggestionName(props: {
  shouldShowHandleInTooltip: boolean;
  suggestion: EntitySuggestion | CustomParty;
}): JSX.Element | null {
  if (isCustomParty(props.suggestion)) {
    return (
      <span
        className={classNames(
          css(styles.customPartyWrapper),
          'u-mini-spacing-right u-flex-none u-ellipsis-text',
          {
            'u-faded': false,
          }
        )}
      >
        {props.suggestion.text}{' '}
      </span>
    );
  } else if (props.shouldShowHandleInTooltip) {
    return (
      <Tooltip
        text={`@${props.suggestion.handle}`}
        placement={FloatingPlacement.TOP}
        disableContentSelection={true}
      >
        <span
          className={classNames('u-mini-spacing-right u-flex-grow u-ellipsis-text', {
            'u-faded': props.suggestion.suspended,
          })}
        >
          {props.suggestion.name}{' '}
        </span>
      </Tooltip>
    );
  } else if (props.suggestion.name) {
    return (
      <span
        className={classNames('u-mini-spacing-right u-flex-none u-ellipsis-text', {
          'u-faded': props.suggestion.suspended,
        })}
      >
        {props.suggestion.name}{' '}
      </span>
    );
  }
  return null;
}

function EntitySuggestionHandle(props: {
  shouldShowHandleInTooltip: boolean;
  suggestion: EntitySuggestion;
}): JSX.Element | null {
  return props.shouldShowHandleInTooltip ? null : (
    <span className="u-flex-grow u-faded">@{props.suggestion.handle} </span>
  );
}

function EntitySuggestionType(props: {suggestion: EntitySuggestion}): JSX.Element | null {
  if (!props.suggestion.type) {
    return null;
  }
  return (
    <span className="u-mini-spacing-right u-faded u-flex-none">{typeToName(props.suggestion.type)}</span>
  );
}

export function EntitySuggestionRow({
  suggestion,
  isSmallInput,
}: {
  suggestion: EntitySuggestion | CustomParty;
  isSmallInput: boolean;
}): JSX.Element {
  const shouldShowHandleInTooltip =
    !isCustomParty(suggestion) &&
    Boolean(suggestion.name) &&
    Boolean(isSmallInput || suggestion.name!.length > 25);

  return (
    <div
      className="u-tiny-padding u-vertical-center-items u-flex-grow u-overflow-hidden"
      onMouseDown={handleMouseDown}
      {...getTestProps({component: 'EntitySuggestionRow', element: 'root'})}
    >
      {isCustomParty(suggestion) || suggestion.handle ? (
        <div className="u-vertical-center-items u-flex-grow u-overflow-hidden">
          {!isCustomParty(suggestion) && (
            <Avatar
              entity={suggestion}
              size={AvatarSize.SMALL}
              className="u-mini-spacing-right u-flex-none"
            />
          )}
          <EntitySuggestionName
            shouldShowHandleInTooltip={shouldShowHandleInTooltip}
            suggestion={suggestion}
          />
          {!isCustomParty(suggestion) && (
            <EntitySuggestionHandle
              shouldShowHandleInTooltip={shouldShowHandleInTooltip}
              suggestion={suggestion}
            />
          )}
          {!isCustomParty(suggestion) && <EntitySuggestionType suggestion={suggestion} />}
        </div>
      ) : (
        <>
          <span className={css(styles.invite)}>
            We did not find a user with that email. Press enter to invite &lt;{suggestion.email}&gt; to
            Benchling.
          </span>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  customPartyWrapper: {
    marginLeft: '34px',
  },
  invite: {
    color: NEAR_BLACK,
    ...FONT_PROXIMA_11,
  },
});
